/**
 * Runtime Zod schema for `gencow.config.ts`.
 *
 * Lives here (server) instead of `@gencow/core` because we deliberately keep
 * `@gencow/core` lean — no zod dependency on the user-facing core package.
 *
 * The TypeScript-facing types for users (`GencowConfig`, etc.) still live in
 * `@gencow/core/src/config.ts` as hand-written interfaces. Drift between the
 * two is a known tradeoff accepted in exchange for keeping core small.
 */

import { z } from "zod";
import { normalizeFrontendOrigin } from "./server-origin-policy.js";

const codegenInput = z
  .object({
    clientOutDir: z.string().optional(),
    outDir: z
      .string()
      .optional()
      .describe("Deprecated — use `codegen.clientOutDir`. Still accepted for backwards compatibility."),
    serverOutDir: z.string().optional(),
    authSchema: z
      .union([
        z.boolean(),
        z.object({
          emitRelations: z.boolean().optional(),
        }),
      ])
      .optional()
      .describe("Better Auth schema codegen: false disables it; true or object emits schema-auth.gen.ts."),
  })
  .optional();

const dbInput = z
  .object({
    url: z.string().optional(),
  })
  .optional();

const deployInput = z
  .object({
    app: z.string().optional(),
  })
  .optional();

const cloudFeatureToggleInput = z.union([z.boolean(), z.record(z.string(), z.unknown()), z.undefined()]);

const cloudFeaturesInput = z
  .object({
    rag: cloudFeatureToggleInput.optional(),
    ai: cloudFeatureToggleInput.optional(),
    admin: cloudFeatureToggleInput.optional(),
  })
  .catchall(cloudFeatureToggleInput)
  .optional();

const rawConfigSchema = z.object({
  rootDir: z.string().optional(),
  functionsDir: z.string().optional().describe("Deprecated — use `rootDir`."),
  schema: z.union([z.string(), z.array(z.string())]).optional(),
  codegen: codegenInput,
  cloudFeatures: cloudFeaturesInput,
  storage: z.string().optional(),
  db: dbInput,
  port: z.number().optional(),
  deploy: deployInput,
  name: z.string().optional(),
  frontendOrigins: z.array(z.string()).optional(),
  trustedOrigins: z.array(z.string()).optional(),
});

export const GENCOW_CONFIG_DEFAULTS = Object.freeze({
  rootDir: "./gencow",
  schema: "./gencow/schema.ts",
  codegen: { clientOutDir: "./src/gencow", authSchema: true },
  storage: "./.gencow/uploads",
  db: { url: "./.gencow/data" },
  port: 5456,
  frontendOrigins: [] as readonly string[],
  trustedOrigins: [] as readonly string[],
});

export const gencowConfigSchema = rawConfigSchema.transform((v) => {
  const trimPath = (value: unknown) =>
    typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;

  const rootDir = trimPath(v.rootDir);
  const functionsDir = trimPath(v.functionsDir);
  if (rootDir && functionsDir) {
    throw new Error("[gencow] Invalid gencow.config: set either `rootDir` or `functionsDir`, not both.");
  }
  const resolvedRootDir = rootDir ?? functionsDir ?? GENCOW_CONFIG_DEFAULTS.rootDir;

  const clientCodegenDir =
    trimPath(v.codegen?.clientOutDir) ??
    trimPath(v.codegen?.outDir) ??
    GENCOW_CONFIG_DEFAULTS.codegen.clientOutDir;

  const authSchema = v.codegen?.authSchema ?? GENCOW_CONFIG_DEFAULTS.codegen.authSchema;
  const serverOutDirRaw = v.codegen?.serverOutDir;
  const serverOutDir =
    typeof serverOutDirRaw === "string" && serverOutDirRaw.trim().length > 0
      ? serverOutDirRaw.trim()
      : undefined;

  const codegen: {
    clientOutDir: string;
    /** @deprecated Same value as {@link clientOutDir}; kept for existing callers. */
    outDir: string;
    authSchema: boolean | { emitRelations?: boolean };
    serverOutDir?: string;
  } = {
    clientOutDir: clientCodegenDir,
    outDir: clientCodegenDir,
    authSchema,
  };
  if (serverOutDir !== undefined) {
    codegen.serverOutDir = serverOutDir;
  }

  const result: {
    rootDir: string;
    schema: string | string[];
    codegen: {
      clientOutDir: string;
      outDir: string;
      authSchema: boolean | { emitRelations?: boolean };
      serverOutDir?: string;
    };
    cloudFeatures?: Record<string, boolean | Record<string, unknown> | undefined>;
    storage: string;
    db: { url: string };
    port: number;
    frontendOrigins: string[];
    trustedOrigins: string[];
    deploy?: { app: string };
    name?: string;
  } = {
    rootDir: resolvedRootDir,
    schema: v.schema ?? GENCOW_CONFIG_DEFAULTS.schema,
    codegen,
    storage: v.storage ?? GENCOW_CONFIG_DEFAULTS.storage,
    db: { url: v.db?.url ?? GENCOW_CONFIG_DEFAULTS.db.url },
    port: v.port ?? GENCOW_CONFIG_DEFAULTS.port,
    frontendOrigins: (v.frontendOrigins ?? []).map((origin) => normalizeFrontendOrigin(origin)),
    trustedOrigins: v.trustedOrigins ?? [],
  };

  if (v.deploy?.app) result.deploy = { app: v.deploy.app };
  if (v.name) result.name = v.name;
  if (v.cloudFeatures) result.cloudFeatures = v.cloudFeatures;
  return result;
});

export type GencowConfigParsed = z.output<typeof gencowConfigSchema>;

export function getDefaultConfig(): GencowConfigParsed {
  return gencowConfigSchema.parse({});
}

// ── Compile-time drift guard ──────────────────────────────────────────────
// Force `z.input<typeof gencowConfigSchema>` and `GencowConfig` (hand-written
// in @gencow/core) to stay structurally equivalent. If a field is added or
// removed on either side without updating the other, one of these assertions
// will fail under `tsc`, breaking the server build.
//
// The `import type` is fully erased at compile time — no runtime dep on
// @gencow/core, so the bundled `cli/server/config.js` stays clean.
import type { GencowConfig } from "@gencow/core";
type _SchemaInput = z.input<typeof gencowConfigSchema>;
const _gencowConfigAssignableToSchemaInput: _SchemaInput = {} as GencowConfig;
const _schemaInputAssignableToGencowConfig: GencowConfig = {} as _SchemaInput;
void _gencowConfigAssignableToSchemaInput;
void _schemaInputAssignableToGencowConfig;
